import { createContext, useContext } from 'react';
import { ALL_SECTIONS, type NavSection } from './menyuAdminki';

/**
 * ВЫБРАННЫЙ РАЗДЕЛ — ОДИН ИСТОЧНИК НА ВЕСЬ КАРКАС (`s311`, владелец 09.09.2026).
 *
 * ⛔ ПОЧЕМУ КОНТЕКСТ, А НЕ ВТОРОЕ СОСТОЯНИЕ. Владелец увёл выбор раздела из
 * левого меню в верхнюю панель: «так у нас и место появится впихнуть навигацию
 * и лого и не мешать это в левом меню». Но пункты выбранного раздела рисует
 * по-прежнему сайдбар — значит выбор нужен ОБОИМ. Заведи ему второе состояние
 * в шапке — и однажды шапка покажет «Операционку», а меню под ней останется на
 * «Финансах»: рассинхрон, который человек прочтёт как поломку.
 *
 * ⛔ ВИДИМОСТЬ РАЗДЕЛА СЧИТАЕТСЯ ЗДЕСЬ ЖЕ, И ТОЖЕ ОДИН РАЗ. Право, личная
 * скрытость (`prv3`) и роль решают, есть ли раздел вообще; разложи эту проверку
 * по шапке и меню порознь — и список в шапке однажды покажет раздел, которого в
 * меню нет.
 */
export interface RazdelyContextValue {
  /** Разделы, которые этот человек вообще видит. */
  gruppy: string[];
  /** Выбранный сейчас — по клику владельца, иначе по маршруту. */
  vybrannaya: string;
  vybrat: (label: string) => void;
  /** Видим ли конкретный пункт — общая проверка для шапки и меню. */
  vidimyy: (s: NavSection) => boolean;
  /** Пункт, на котором владелец находится по маршруту. */
  tekushchiyKey: string | undefined;
  /** Раскрытый пункт аккордеона внутри раздела. */
  otkrytyyPunkt: string | null | undefined;
  raskryt: (key: string | null | undefined) => void;
}

/**
 * Раздел, которому принадлежит текущий маршрут (подсветка + авто-раскрытие).
 * Берём САМЫЙ длинный совпавший `to` — иначе /analytics/baseline матчнулся бы и на
 * «Куб» (/analytics), и на «Эталоны» (/analytics/baseline). Длиннейший = точный.
 */
export function activeSectionKey(pathname: string): string | undefined {
  let bestKey: string | undefined;
  let bestLen = -1;

  const podhodit = (to: string) => pathname === to || pathname.startsWith(`${to}/`);

  for (const s of ALL_SECTIONS) {
    if (podhodit(s.to) && s.to.length > bestLen) {
      bestKey = s.key;
      bestLen = s.to.length;
    }

    /*
     * ⛔⛔ РАЗДЕЛ АКТИВЕН И ТОГДА, КОГДА ОТКРЫТ ЕГО ПУНКТ (`s322`, Д46).
     *
     * Владелец: «когда ходишь по меню слева записей — аккордеон закрывается».
     * Разбор: раскрытость считалась ТОЛЬКО по адресу самого раздела, а пункты
     * «Записи» живут на СОСЕДНИХ адресах, а не вложенных: раздел стоит на
     * `/zapis/den`, пункты — на `/zapis/nezakrytoe`, `/zapis/bez-ishoda`,
     * `/zapis/ozhidanie`, `/zapis/roznica`, `/zapis/priyomka`. Условие
     * «начинается с `/zapis/den/`» на них не срабатывало ни разу — раздел
     * схлопывался, подсветка гасла, а заодно уезжала и выбранная группа.
     *
     * ⛔ ПОЧЕМУ НЕ ПЕРЕВЕСИТЬ РАЗДЕЛ НА `/zapis`. Такого экрана нет: нажатие на
     * раздел обязано открывать журнал дня. Подчинять адреса вёрстке меню —
     * значит заводить второе правило там, где хватает одного: раздел активен,
     * если активен ОН САМ или любой его пункт. У «Клиенток» пункт вложен и
     * попадал под прежнее условие случайно — теперь оба случая одним законом.
     *
     * ⛔ ДЛИНА СЧИТАЕТСЯ ПО СОВПАВШЕМУ ПУНКТУ, а не по адресу раздела: иначе
     * короткий родитель перебивал бы точное совпадение соседнего раздела.
     */
    for (const d of s.children ?? []) {
      if (podhodit(d.to) && d.to.length > bestLen) {
        bestKey = s.key;
        bestLen = d.to.length;
      }
    }
  }

  return bestKey;
}

export const Ctx = createContext<RazdelyContextValue | null>(null);


export function useRazdely(): RazdelyContextValue {
  const v = useContext(Ctx);

  if (v === null) {
    throw new Error('useRazdely вызван вне RazdelyProvider — каркас обязан обернуть шапку и меню.');
  }

  return v;
}
